import { Table as AntTable } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import compact from 'lodash/compact';
import get from 'lodash/get';
import moment from 'moment';
import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Approval } from 'api/approval';
import { ProjectId, TaskId } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import { DueDate } from 'components/atoms/due-date';
import { Trigger } from 'components/molecules/squad-picker/trigger';
import { useProjectSquad } from 'components/molecules/squad-picker/use-squad-options';
import { squad, squadToOptions } from 'components/molecules/squad-picker/utils';
import { selectActiveStatusUpdate } from 'data/status-updates';
import { Task } from 'data/tasks';
import { TaskDrawerTab, useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useTranslation } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { ApprovalCell } from '../tasks/approval-cell';
import styles from './resource-list-table.module.css';

interface Props {
  size: SizeType;
  data: Approval[];
  renderExtraActions?: (data) => React.ReactNode;
}

interface ApprovalWithTask extends Approval {
  task: Task;
}

const StyledAvatar = styled(Avatar)`
  margin-right: var(--spacing-04);
`;

export const ApprovalTable: FC<Props> = (props) => {
  const { data, size, renderExtraActions } = props;
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  // TODO: @asimkt why is this possibly undefined?!
  const projectId = activeStatusUpdate?.project.projectId;
  const {
    data: { users },
  } = useProjectSquad(ProjectId(projectId!));
  const navigateToTask = useNavigateToTask();

  const getUser = useCallback(
    (userId: number) => {
      return users.find((user) => user.userId === userId);
    },
    [users],
  );

  const onApprovalClick = (task: Task) => {
    navigateToTask(TaskId(task.project, task.taskId), {
      tab: TaskDrawerTab.Approvals,
    });
  };
  const COLUMNS = [
    {
      title: useTranslation('approvals.request_note'),
      className: styles.task_name,
      dataIndex: 'taskName',
      key: 'taskName',
      render: (text, record: ApprovalWithTask) => {
        const comments = get(record, ['comments']);
        const user = record.requestBy;
        const userComment =
          comments?.find((c) => c.createdBy === user.userId) || {};
        const approvalNote =
          get(userComment, ['content'], '').replace(/(<([^>]+)>)/gi, '') || '';

        return (
          <div className={styles.cell_title}>
            <StyledAvatar name={fullName(user)} src={user.photoUrl} />
            <ApprovalCell
              size={size}
              name={approvalNote || get(record, ['task', 'taskName'], '')}
              task={record.task}
              onClick={onApprovalClick}
            />
          </div>
        );
      },
    },
    {
      title: useTranslation('approvals.approvers'),
      className: styles.assignee,
      width: '140px',
      render: (value, record: ApprovalWithTask) => {
        const approvers = record.requestTo.map(({ userId }) => {
          return getUser(userId);
        });

        return (
          <Trigger
            data-email-template-nowrap
            value={squadToOptions(squad({ users: compact(approvers) }))}
            size="small"
          />
        );
      },
    },
    {
      title: useTranslation('due_date'),
      className: styles.due_date,
      width: '140px',
      render: (_text, record: ApprovalWithTask) => (
        <div className={styles.cell} data-email-template-nowrap>
          {record.dueDate ? (
            <DueDate timestamp={moment(record.dueDate)} />
          ) : null}
          {renderExtraActions ? renderExtraActions(record) : null}
        </div>
      ),
    },
  ];

  return (
    <AntTable
      size={size}
      rowKey="taskId"
      className={styles.table}
      rowClassName={styles.row}
      pagination={false}
      columns={COLUMNS}
      dataSource={data as ApprovalWithTask[]}
    />
  );
};
